import { ref } from 'vue';
import type { Ref } from 'vue';
import { useToast } from 'vue-toastification';

// Typdefinitionen für Nachrichten und Status
export interface ChatMessage {
  id?: number;
  message_id?: number;
  sender_id: number;
  recipient_id?: number;
  recipient_type: 'user' | 'group' | 'all';
  message: string;
  sender_name?: string;
  sender_vorname?: string;
  sender_nachname?: string;
  group_name?: string;
  created_at?: string;
  read_status?: number;
  _temp?: boolean;
  message_uuid?: string;
  self_sent?: boolean;
}

// Verhindert doppelte Benachrichtigungen mit einem Set für die letzten Nachrichten
const processedMessages = new Set<string>();
const MESSAGE_EXPIRY_TIME = 60000; // 60 Sekunden

// Entferne ältere Nachrichten aus dem Set
setInterval(() => {
  if (processedMessages.size > 50) {
    // Wenn Set zu groß wird, ältere Einträge entfernen
    const oldest = Array.from(processedMessages)[0];
    processedMessages.delete(oldest);
  }
}, MESSAGE_EXPIRY_TIME);

// Als reaktive Variablen definieren
const unreadMessages: Ref<number> = ref(0);
const unreadChats: Ref<Map<string, number>> = ref(new Map()); // Map von Chat-IDs zu Nachrichtenzählern
let notificationSound: HTMLAudioElement | null = null; // Speichern des Audio-Elements

export default {
  unreadMessages,
  unreadChats,

  // Neue Nachricht empfangen - mit Schutz vor Duplikaten
  newMessage(message: ChatMessage, isActive = false): void {
    // Erstelle eine eindeutige ID für die Nachricht
    const messageKey = `${message.sender_id}_${message.recipient_id}_${message.recipient_type}_${message.message.substring(0, 20)}_${Date.now()}`;

    // Prüfe, ob diese Nachricht bereits verarbeitet wurde
    if (processedMessages.has(messageKey)) {
      return;
    }

    // Nachricht als verarbeitet markieren
    processedMessages.add(messageKey);

    // Wenn es eine selbst gesendete Nachricht ist, nicht als ungelesen zählen
    if (message.self_sent) return;

    // Wenn der Chat nicht aktiv ist, erhöhen wir den Zähler
    if (!isActive) {
      const chatId: string = message.recipient_type === 'user'
        ? (message.sender_id || message.recipient_id || '').toString()
        : `${message.recipient_type}_${message.recipient_id}`;

      const currentCount: number = unreadChats.value.get(chatId) || 0;
      unreadChats.value.set(chatId, currentCount + 1);
      unreadMessages.value++;

      // Benachrichtigung anzeigen
      this.showNotification(message);
    }
  },

  // Nachricht als gelesen markieren
  markAsRead(chatId: string): void {

    if (this.unreadChats.value.has(chatId)) {
      const count = this.unreadChats.value.get(chatId) || 0;
      // Zähler reduzieren
      this.unreadMessages.value = Math.max(0, this.unreadMessages.value - count);
      // Chat aus der Map entfernen
      this.unreadChats.value.delete(chatId);

    }

    // Explizit reaktive Aktualisierung erzwingen für die Map
    this.unreadChats.value = new Map(this.unreadChats.value);
  },

// Benachrichtigung anzeigen
showNotification(message: ChatMessage): void {
  const toast = useToast();

  // Prüfen, ob es eine Gruppennachricht ist
  const isGroupMessage: boolean = message.recipient_type === 'group';

  // Absender-Name aufbereiten
  const sender: string = message.sender_vorname && message.sender_nachname
    ? `${message.sender_vorname} ${message.sender_nachname}`
    : message.sender_name || 'Jemand';

  // Titel anpassen je nach Nachrichtentyp
  const title: string = isGroupMessage
    ? `Neue Gruppennachricht in ${message.group_name || 'Gruppe'}`
    : 'Neue Nachricht';

  // PRIVATSPHÄRE: Kein Nachrichteninhalt mehr in der Vorschau
  const previewText: string = isGroupMessage
    ? `${sender} hat in "${message.group_name || 'Gruppe'}" geschrieben`
    : `${sender} hat dir eine Nachricht gesendet`;

  // Desktop-Benachrichtigung, falls unterstützt
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      // Auch hier keine Nachrichtendetails
      body: previewText,
      icon: '/favicon.ico'
    });
  }

  // Toast-Benachrichtigung (privatsphärefreundlich)
  toast.info(previewText, {
    timeout: 5000,
    closeButton: 'button',
    onClick: () => {
      // Zum Chat navigieren
      window.location.href = '/chat';
    }
  });

  // Ton abspielen - verbesserte Version
  try {
    // Statisches Audio-Element erstellen, wenn noch nicht vorhanden
    if (!notificationSound) {
      notificationSound = new Audio('/sounds/message-notification.mp3');
      notificationSound.volume = 0.5;
    }

    // Audio zurücksetzen und abspielen
    notificationSound.currentTime = 0;
    const playPromise = notificationSound.play();

    // Fehlerbehandlung für Browser-Einschränkungen
    if (playPromise !== undefined) {
      playPromise.catch(e => {
        console.error('Fehler beim Abspielen des Benachrichtigungstons:', e);
      });
    }
  } catch (e) {
    console.error('Fehler beim Laden des Benachrichtigungstons:', e);
  }
}
};
